'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { FC, useRef } from 'react';
import Image from '@/components/Image';
import LeadMagnetPills from '@/components/LeadMagnet/Intro/Pills';
import AnimatedButton from '@/components/shared/AnimatedButton';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { buildEntrance, splitTitleLines } from '@/lib/lead-magnet/animation';
import { LeadMagnetIntro as LeadMagnetIntroModel, LeadMagnetMedia } from '@/types/lead-magnet';
import css from './style.module.scss';
interface Props {
  intro: LeadMagnetIntroModel;
  onStart?: () => void;
  questionsBackground?: LeadMagnetMedia | null;
}
const LeadMagnetIntro: FC<Props> = t0 => {
  const $ = _c(29);
  const {
    intro,
    onStart,
    questionsBackground
  } = t0;
  const {
    eyebrow,
    title,
    description,
    pills,
    ctaLabel,
    visual
  } = intro;
  const rootRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      const titleSplit = splitTitleLines(root, css.title);
      const timeline = buildEntrance([{
        targets: root.querySelector(`.${css.visualImage}`),
        role: "image",
        position: 0,
        duration: 1.3,
        vars: {
          ease: "power2.out"
        }
      }, {
        targets: titleSplit.lines,
        role: "title",
        position: 0.1,
        duration: 1,
        stagger: 0.13
      }, {
        targets: root.querySelector(`.${css.eyebrow}`),
        position: 0.22,
        duration: 0.6
      }, {
        targets: root.querySelector(`.${css.description}`),
        position: 0.4,
        duration: 0.7
      }, {
        targets: root.querySelectorAll(`.${css.pills} > *`),
        role: "control",
        position: 0.58,
        duration: 0.55,
        stagger: 0.08
      }, {
        targets: root.querySelector(`.${css.ctaWrapper}`),
        role: "control",
        position: 0.86,
        duration: 0.6
      }]);
      timeline.eventCallback("onComplete", titleSplit.revert);
      return titleSplit.revert;
    };
    t2 = {
      scope: rootRef
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useGSAP(t1, t2);
  let t3;
  if ($[2] !== eyebrow) {
    t3 = eyebrow ? <Typography uppercase={true} variant="body-m" tag="p" className={css.eyebrow}>{eyebrow}</Typography> : null;
    $[2] = eyebrow;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== title) {
    t4 = <Typography uppercase={true} variant="h1" tag="h1" className={css.title}><StrapiRichContent isInlineContent={true} content={title} /></Typography>;
    $[4] = title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== description) {
    t5 = description ? <Typography variant="body-m" tag="div" className={css.description}><StrapiRichContent content={description} /></Typography> : null;
    $[6] = description;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== pills) {
    t6 = <LeadMagnetPills pills={pills} className={css.pills} />;
    $[8] = pills;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== ctaLabel || $[11] !== onStart) {
    t7 = <AnimatedButton wrapperClassName={css.ctaWrapper} className={css.cta} classNameText={css.ctaLabel} onClick={onStart}>{ctaLabel}</AnimatedButton>;
    $[10] = ctaLabel;
    $[11] = onStart;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className={css.content}>{t3}{t4}{t5}{t6}{t7}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== questionsBackground) {
    t9 = questionsBackground ? {
      "--questions-bg": `url(${questionsBackground.url})`
    } as React.CSSProperties : undefined;
    $[19] = questionsBackground;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== visual) {
    t10 = visual ? <Image preload={true} className={css.visualImage} width={visual.width ?? 525} height={visual.height ?? 468} src={visual.url} alt={visual.alt} sizes="(max-width: 991px) 100vw, 33vw" /> : null;
    $[21] = visual;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className={css.visual} style={t9}>{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t8) {
    t12 = <section ref={rootRef} className={css.intro}>{t8}{t11}</section>;
    $[26] = t11;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};
export default LeadMagnetIntro;